import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Loader2, Clock, Globe2, Building2, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const TYPE_COLORS = { initial: "bg-blue-100 text-blue-800", annual: "bg-teal-100 text-teal-800", continuing_education: "bg-amber-100 text-amber-800" };

export default function TrainingMarketplace() {
  const [filters, setFilters] = useState(null);
  const [sel, setSel] = useState({ state: "", role: "", type: "", language: "", provider: "" });
  const [q, setQ] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enroll, setEnroll] = useState(null);   // course being enrolled
  const [seats, setSeats] = useState(1);
  const [emails, setEmails] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/training/checkout", {
        course_id: enroll.id, seats: Number(seats) || 1,
        mode: (Number(seats) || 1) > 1 ? "bulk" : "self",
        origin_url: window.location.origin,
        assignee_emails: emails.split(/[\n,]/).map((e) => e.trim()).filter(Boolean),
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start checkout"); setCheckingOut(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(sel).forEach(([k, v]) => { if (v) params.append(k, v); });
    if (q) params.append("q", q);
    api.get(`/training/courses?${params.toString()}`).then((r) => setCourses(r.data.courses)).catch(() => {}).finally(() => setLoading(false));
  }, [sel, q]);

  useEffect(() => { api.get("/training/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const typeName = (id) => filters?.types.find((t) => t.id === id)?.name || id;
  const reset = () => { setSel({ state: "", role: "", type: "", language: "", provider: "" }); setQ(""); };

  const FilterSelect = ({ k, placeholder, items, valueKey = "id", labelKey = "name" }) => (
    <Select value={sel[k] || "all"} onValueChange={(v) => setSel({ ...sel, [k]: v === "all" ? "" : v })}>
      <SelectTrigger data-testid={`training-${k}`} className="bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72"><SelectItem value="all">{placeholder}</SelectItem>
        {items?.map((it) => { const val = typeof it === "string" ? it : it[valueKey]; const lab = typeof it === "string" ? it : (it[labelKey] || it.name); return <SelectItem key={val} value={val}>{lab}</SelectItem>; })}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> National Training Marketplace</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-3xl leading-tight">Training for every state, role and requirement</h1>
          <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">Original, licensed, third-party and state-approved courses — filter by state, worker role, initial / annual / CE, language and provider.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="space-y-3 h-fit lg:sticky lg:top-24" data-testid="training-filters">
            <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input data-testid="training-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses…" className="bg-white pl-9" /></div>
            {filters && <>
              <FilterSelect k="state" placeholder="All states" items={filters.states} valueKey="slug" />
              <FilterSelect k="role" placeholder="All worker roles" items={filters.roles} />
              <FilterSelect k="type" placeholder="Initial / Annual / CE" items={filters.types} />
              <FilterSelect k="language" placeholder="All languages" items={filters.languages} />
              <FilterSelect k="provider" placeholder="All providers" items={filters.providers} />
            </>}
            <Button variant="outline" onClick={reset} data-testid="training-reset" className="w-full">Reset filters</Button>
          </aside>

          <div>
            <p className="text-sm text-slate-500 mb-4">{loading ? "Loading…" : `${courses.length} course${courses.length !== 1 ? "s" : ""}`}</p>
            {loading ? <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div> : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="training-grid">
                {courses.map((c, i) => (
                  <motion.div key={c.id} data-testid={`training-course-${c.id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                    className="bg-white rounded-2xl border border-blue-900/5 shadow-sm overflow-hidden flex flex-col">
                    <img src={c.image} alt={c.title} className="h-36 w-full object-cover" />
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${TYPE_COLORS[c.training_type]}`}>{typeName(c.training_type)}</span>
                        {c.ce_hours > 0 && <span className="text-[11px] text-slate-500 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {c.ce_hours} hrs</span>}
                      </div>
                      <h3 className="font-display font-semibold text-blue-950 leading-snug">{c.title}</h3>
                      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed line-clamp-2 flex-1">{c.description}</p>
                      <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.provider}</span>
                        <span className="flex items-center gap-1"><Globe2 className="w-3 h-3" /> {c.language}</span>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <span className="font-display font-bold text-blue-950">${c.price.toFixed(0)}</span>
                        <Button size="sm" onClick={() => { setEnroll(c); setSeats(1); setEmails(""); setCheckingOut(false); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`training-enroll-${c.id}`}>Enroll</Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {courses.length === 0 && <div className="sm:col-span-2 xl:col-span-3 text-center py-16 bg-white rounded-2xl border border-blue-900/5"><p className="text-slate-500">No courses match these filters.</p></div>}
              </div>
            )}
          </div>
        </div>
      </section>

      <Dialog open={!!enroll} onOpenChange={(o) => !o && setEnroll(null)}>
        <DialogContent data-testid="enroll-dialog">
          <DialogHeader><DialogTitle>Enroll in {enroll?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">${enroll?.price?.toFixed(0)} per seat · {enroll?.provider}</p>
            <div>
              <Label>Number of seats</Label>
              <Input type="number" min={1} max={100} value={seats} onChange={(e) => setSeats(e.target.value)} data-testid="enroll-seats" className="mt-1 w-32" />
              <p className="text-xs text-slate-400 mt-1">Buy multiple seats to assign training to your team.</p>
            </div>
            {Number(seats) > 1 && (
              <div>
                <Label>Invite team members (optional)</Label>
                <Textarea value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="enroll-emails" rows={3} className="mt-1" placeholder="one email per line — you can also assign later from Course Seats" />
              </div>
            )}
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
              <span className="text-sm text-slate-600 flex items-center gap-1.5"><Users className="w-4 h-4" /> {seats} seat{Number(seats) > 1 ? "s" : ""}</span>
              <span className="font-display font-bold text-blue-950">${((enroll?.price || 0) * (Number(seats) || 1)).toFixed(0)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={startCheckout} disabled={checkingOut} data-testid="enroll-checkout" className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11">
              {checkingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : "Proceed to secure checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
